import tenantTheme from '@theme';
import { Avatar, InputNumber } from 'antd';
import debounce from 'lodash/debounce';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button, Drawer, Flex, Icon, LoaderWrapper, TextInput, notification } from '../common';
import { formatWholeNumber } from '../../utility/utility';
import { getInitials } from '../listing/change-owner/assignee-radio-grid';
import {
  QUOTA_OWNER_ROLE,
  QUOTA_SEARCH_DEBOUNCE_MS,
  clampAssignDelta,
  getAgentProductUnits,
  getHeldQuotaProducts,
  getQuotaPool,
  getQuotaSideDrawerProps,
  getQuotaTransferBatches,
  isSameId,
} from '../../utility/quota';
import {
  useGetQuotaAgentsQuery,
  useGetQuotaCreditsWidgetDataQuery,
  useTransferProductQuotaMutation,
} from '../../apis/quotaCredits';
import { QuotaPoolSplit } from '../widgets/quota-products/agency-quota-header';
import { Chip, StackBar } from '../widgets/quota-products/styled';
import ExpandableBottomSheet from './expandable-bottom-sheet';
import { AgentCard, AssignTable, PoolCard } from './styled';

// Agents listed at once in the drawer.
const AGENTS_PER_PAGE = 100;

// "Assign Available Products" drawer (a bottom sheet on mobile): pick an agent, then change what they hold per
// product. Changes are deltas: positive units go owner -> agent, negative ones are taken back (credits/transfer,
// one direction per call).
const AssignProductsDrawer = ({ isVisible, setIsVisible, agentId }) => {
  const { t } = useTranslation();
  const ref = useRef();
  const { locale, isMobile } = useSelector((state) => state.app.AppConfig);
  const loggedInUser = useSelector((state) => state.app.loginUser.user);
  const [selectedId, setSelectedId] = useState(agentId);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [deltas, setDeltas] = useState({});
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const updateDebouncedSearch = useMemo(() => debounce(setDebouncedSearch, QUOTA_SEARCH_DEBOUNCE_MS), []);
  useEffect(() => () => updateDebouncedSearch.cancel(), [updateDebouncedSearch]);

  const { data: summary } = useGetQuotaCreditsWidgetDataQuery(
    { platform_id: [loggedInUser?.platforms?.[0]?.id] },
    { skip: !isVisible },
  );
  const { data: agentsData, isFetching: agentsLoading } = useGetQuotaAgentsQuery(
    { page: 1, perPage: AGENTS_PER_PAGE, search: debouncedSearch },
    { skip: !isVisible },
  );
  const [transferProductQuota, { isLoading: saving }] = useTransferProductQuotaMutation();

  const allRows = agentsData?.agents || [];
  const agents = useMemo(() => allRows.filter((e) => !e.isOwner), [allRows]);
  const ownerId =
    allRows.find((e) => e.isOwner)?.id ??
    (loggedInUser?.user_role_within_agency === QUOTA_OWNER_ROLE ? loggedInUser.id : undefined);

  useEffect(() => {
    if (isVisible) {
      setSelectedId(agentId);
      setDeltas({});
      setSearch('');
      updateDebouncedSearch.cancel();
      setDebouncedSearch('');
      ref.current?.openDrawer();
    }
  }, [isVisible, agentId]);

  // Keep the chosen agent while the list is searched (the search may filter them out).
  useEffect(() => {
    const found = agents.find((e) => isSameId(e.id, selectedId));
    if (found) setSelectedAgent(found);
    else if (!selectedId) setSelectedAgent(null);
  }, [agents, selectedId]);

  const products = summary?.products || [];
  const core = summary?.core || [];
  const addOns = summary?.others || [];
  const totals = summary?.totals || {};
  const has = (product) => getAgentProductUnits(selectedAgent, product.id);
  const delta = (product) => deltas[product.id] || 0;
  const changes = products.filter((product) => delta(product) !== 0);

  const setDelta = (product, value) => {
    const next = clampAssignDelta(value, has(product), getQuotaPool(product));
    setDeltas((prev) => ({ ...prev, [product.id]: next }));
  };

  const close = () => ref.current?.closeDrawer();

  const pickAgent = (agent) => {
    setSelectedId(agent?.id);
    setSelectedAgent(agent || null);
    setDeltas({});
  };

  const save = async () => {
    if (!changes.length || !selectedAgent) return;
    if (!ownerId) {
      notification.error(t('Something went wrong, please try again'));
      return;
    }
    const { takeBack, assign } = getQuotaTransferBatches({ changes, deltas, agentId: selectedAgent.id, ownerId });
    const transfer = (items) => (items.length ? transferProductQuota({ products: items }) : null);
    // Take back first so the pool holds those units before anything is assigned.
    const takeBackResponse = await transfer(takeBack);
    if (takeBackResponse?.error) {
      notification.error(takeBackResponse.error);
      return;
    }
    const assignResponse = await transfer(assign);
    if (assignResponse?.error) {
      // The take-backs went through: drop them so a retry only sends the assignments.
      setDeltas((prev) => Object.fromEntries(Object.entries(prev).filter(([, value]) => value > 0)));
      notification.error(assignResponse.error);
      return;
    }
    notification.success(t('Products updated'));
    close();
  };

  const stepButtonStyle = {
    height: isMobile ? 30 : 32,
    width: isMobile ? 30 : 32,
    border: 'none',
    color: tenantTheme['primary-color'],
    ...(isMobile && { background: 'transparent' }),
  };

  const renderAgentList = () => (
    <Flex vertical gap="16px">
      <PoolCard>
        <Flex
          justify="space-between"
          align={isMobile ? 'start' : 'baseline'}
          gap={isMobile ? '2px' : '12px'}
          vertical={isMobile}
        >
          <span style={{ whiteSpace: 'nowrap' }}>
            <b className="pool-big">{formatWholeNumber(totals.notAssigned)}</b> {t('not assigned yet')}
          </span>
          <span className="pool-sub">
            {t('of {{total}} in your package', { total: formatWholeNumber(totals.total) })}
          </span>
        </Flex>
        {isMobile ? (
          <QuotaPoolSplit totals={totals} />
        ) : (
          <StackBar $height={8}>
            <i style={{ flex: `${totals.notAssigned || 0} 1 0`, background: tenantTheme['primary-color'] }} />
            <i style={{ flex: `${Math.max((totals.total || 0) - (totals.notAssigned || 0), 0)} 1 0` }} />
          </StackBar>
        )}
        <div className={isMobile ? 'pool-cells is-mobile' : 'pool-cells'}>
          {core.map((product) => (
            <div key={product.id}>
              <span className="pool-ic" style={{ '--ic-tint': product.iconColor }}>
                <Icon icon={product.icon} size="12px" color={product.iconColor} />
              </span>
              <b>{formatWholeNumber(getQuotaPool(product))}</b>
              <span className="pool-label">{t(product.name)}</span>
            </div>
          ))}
          {!!addOns.length && (
            <div>
              <span className="pool-ic" style={{ '--ic-tint': tenantTheme['gray-dark-color'] }}>
                <Icon icon="BsGrid" size="12px" color={tenantTheme['gray-dark-color']} />
              </span>
              <b>{formatWholeNumber(getQuotaPool(summary.othersTotals))}</b>
              <span className="pool-label">{t('Add-ons')}</span>
            </div>
          )}
        </div>
      </PoolCard>
      <TextInput
        allowClear
        prefixIcon="FiSearch"
        iconColor={tenantTheme['text-color-secondary']}
        fieldSize="large"
        placeholder={t('Search agents by name or ID')}
        value={search}
        handleChange={(e) => {
          setSearch(e.target.value);
          updateDebouncedSearch(e.target.value);
        }}
      />
      <b>{t('Who are you assigning to?')}</b>
      <LoaderWrapper loading={agentsLoading}>
        <Flex vertical gap="8px">
          {agents.map((agent) => {
            const held = getHeldQuotaProducts(agent.products);
            return (
              <AgentCard
                key={agent.id}
                as="button"
                type="button"
                className={isMobile ? 'is-click is-row' : 'is-click'}
                onClick={() => pickAgent(agent)}
              >
                <Avatar className="avatar">{getInitials(agent.name)}</Avatar>
                <div className="agent-body">
                  <b>{agent.name}</b>
                  <small>
                    {t('ID {{id}}', { id: agent.id })}
                    {(!!held.length || !!agent.used) &&
                      ` · ${t('{{count}} used so far', { count: formatWholeNumber(agent.used) })}`}
                  </small>
                  <Flex wrap gap="6px" className="mt-8 agent-chips">
                    {held.length ? (
                      held.map((product) => (
                        <Chip key={product.id} $small>
                          <Icon icon={product.icon} size="12px" color={product.iconColor} />
                          <b>{formatWholeNumber(product.available)}</b>/{formatWholeNumber(product.total)}{' '}
                          <span className="chip-name">{t(product.name)}</span>
                        </Chip>
                      ))
                    ) : (
                      <small>{t('Nothing assigned yet')}</small>
                    )}
                  </Flex>
                </div>
                <Icon icon="FiChevronRight" size="18px" color={tenantTheme['text-color-secondary']} className="flipX" />
              </AgentCard>
            );
          })}
          {!agentsLoading && !agents.length && <span className="color-gray-dark">{t('No Record Found')}</span>}
        </Flex>
      </LoaderWrapper>
    </Flex>
  );

  const renderProductRow = (product) => {
    const value = delta(product);
    const pool = getQuotaPool(product);
    return (
      <div className={isMobile ? 'assign-row is-mobile' : 'assign-row'} key={product.id}>
        <div className="assign-name">
          <Icon icon={product.icon} size="18px" color={product.iconColor} />
          <div>
            <b>{t(product.name)}</b>
            <small>{t('{{count}} not assigned', { count: formatWholeNumber(pool) })}</small>
          </div>
        </div>
        {!isMobile && <b className="assign-has">{formatWholeNumber(has(product))}</b>}
        <Flex align="center" gap="6px" className="assign-change">
          {/* mobile: − value + in one compact pill; Arabic mirrors it (+ value −), the number stays left to right */}
          <Flex align="center" className={isMobile ? 'assign-pill' : undefined} gap={isMobile ? '0px' : '6px'}>
            <Button
              type="primary-light"
              icon="HiMinus"
              iconSize="1em"
              aria-label={t('Decrease')}
              disabled={value <= -has(product)}
              onClick={() => setDelta(product, value - 1)}
              style={stepButtonStyle}
            />
            {/* Out-of-range typing (e.g. a take-back past what the agent holds) is clamped on blur or Enter */}
            <InputNumber
              inputMode="numeric"
              variant={isMobile ? 'borderless' : undefined}
              controls={false}
              precision={0}
              min={-has(product)}
              max={pool}
              value={value}
              onFocus={(e) => e.target.select()}
              onChange={(next) => next != null && setDelta(product, next)}
              style={{ width: isMobile ? 40 : 64 }}
            />
            <Button
              type="primary-light"
              icon="HiPlus"
              iconSize="1em"
              aria-label={t('Increase')}
              disabled={value >= pool}
              onClick={() => setDelta(product, value + 1)}
              style={stepButtonStyle}
            />
          </Flex>
          <Button
            type="primaryOutlined"
            size="small"
            disabled={!pool || value === pool}
            onClick={() => setDelta(product, pool)}
          >
            {t('Max')}
          </Button>
        </Flex>
        {!isMobile && (
          <div className="assign-after">
            <b style={{ color: value ? tenantTheme['primary-color'] : undefined }}>
              {formatWholeNumber(has(product) + value)}
            </b>
            <small>{t('{{count}} left to assign', { count: formatWholeNumber(pool - value) })}</small>
          </div>
        )}
      </div>
    );
  };

  const renderAgentProducts = () => (
    <Flex vertical gap="16px">
      <AgentCard>
        <Avatar className="avatar">{getInitials(selectedAgent.name)}</Avatar>
        <div className="agent-body">
          <b>{selectedAgent.name}</b>
          <small>
            {t('ID {{id}}', { id: selectedAgent.id })} ·{' '}
            {t('{{count}} available', { count: formatWholeNumber(selectedAgent.available) })} ·{' '}
            {t('{{count}} used', { count: formatWholeNumber(selectedAgent.used) })}
          </small>
        </div>
        <Button type="primaryOutlined" size="small" onClick={() => pickAgent(null)}>
          {t('Change')}
        </Button>
      </AgentCard>
      <AssignTable $mobile={isMobile}>
        {isMobile ? (
          <div className="assign-row is-mobile assign-head">
            <span>{t('Product')}</span>
            <span>{t('Change')}</span>
          </div>
        ) : (
          <div className="assign-row assign-head">
            <span>{t('Product')}</span>
            <span>{t('Has')}</span>
            <span>{t('Change')}</span>
            <span className="text-right">{t('After')}</span>
          </div>
        )}
        <div className="assign-section">{t('Listings and upgrades')}</div>
        {core.map(renderProductRow)}
        {!!addOns.length && <div className="assign-section">{t('Add-ons')}</div>}
        {addOns.map(renderProductRow)}
      </AssignTable>
    </Flex>
  );

  const footer = selectedAgent ? (
    <Flex
      align={isMobile ? 'stretch' : 'center'}
      justify="space-between"
      gap={isMobile ? '10px' : '12px'}
      vertical={isMobile}
    >
      <Flex wrap gap="6px" style={{ minWidth: 0 }}>
        {changes.length ? (
          changes.map((product) => (
            <Chip key={product.id} $small>
              <Icon icon={product.icon} size="12px" color={product.iconColor} />
              {/* ltr: the sign stays left of the number in Arabic ("-11", not "11-") */}
              <b dir="ltr">
                {delta(product) > 0 ? '+' : ''}
                {delta(product)}
              </b>{' '}
              {t(product.name)}
            </Chip>
          ))
        ) : (
          <span style={{ color: tenantTheme['text-color-secondary'] }}>{t('No changes yet')}</span>
        )}
      </Flex>
      <Flex gap="10px" className={isMobile ? 'w-100' : undefined} style={{ flex: 'none' }}>
        <Button type="default" onClick={close} className={isMobile ? 'w-100' : undefined}>
          {t('Cancel')}
        </Button>
        <Button
          type="primary"
          disabled={!changes.length}
          loading={saving}
          onClick={save}
          className={isMobile ? 'w-100' : undefined}
        >
          {t('Save')}
        </Button>
      </Flex>
    </Flex>
  ) : null;

  const Container = isMobile ? ExpandableBottomSheet : Drawer;
  return (
    <Container
      ref={ref}
      {...(!isMobile && getQuotaSideDrawerProps(locale))}
      onCloseDrawer={() => setIsVisible(false)}
      footer={footer}
      title={
        <Flex align="center" gap="8px">
          {!!selectedAgent && (
            <Button
              type="primary-light"
              shape="circle"
              size="small"
              icon="FiArrowLeft"
              iconClassName="flipX"
              aria-label={t('Back')}
              onClick={() => pickAgent(null)}
            />
          )}
          <span className="fw-700">{t('Assign Available Products')}</span>
        </Flex>
      }
    >
      {selectedAgent ? renderAgentProducts() : renderAgentList()}
    </Container>
  );
};

export default AssignProductsDrawer;
